/**
 * SiteCardLogo — a single, small brand mark for the sidebar SITE-LIST
 * cards (site name + location + AC + pathway summary).
 *
 * Site-list rule (distinct from `ReportCardLogos`, which shows BOTH
 * marks for a customer-facing report):
 *   - The site belongs to a CUSTOMER (audience 'customer' + a
 *     customerLogoUrl is present) → show the CUSTOMER logo ONLY.
 *   - Otherwise → show the COMPANY (internal / primary) logo.
 *
 * Branding is resolved through the same `resolveReportBranding` helper
 * the report covers + report cards consume, so the company logo (report
 * theme `logoUrl`) and the customer logo (`site.customerLogoUrl`) stay
 * consistent across surfaces. This component only CONSUMES the resolved
 * branding — it never mutates the resolver.
 *
 * Graceful fallback: the mark renders only when a URL is present, and an
 * `onError` handler hides the <img> if the asset fails to load so a site
 * card never shows a broken-image glyph.
 *
 * Dark-mode legibility: uploaded logos are authored for white paper and would
 * vanish on the near-black sidebar. The <img> is seated on a `LogoBackingChip`
 * so in DARK mode it sits on a subtle always-light chip (brand colours stay
 * intact — no CSS inversion); in LIGHT mode the chip is transparent so the
 * card looks unchanged.
 */
import React from 'react';
import type { ResolvedReportBranding } from '../../lib/reportBranding';
import { LogoBackingChip } from './LogoBackingChip';
import { toRelativeLogoMediaUrl } from '../../lib/logoStorageUrl';

interface SiteCardLogoProps {
  branding: Pick<
    ResolvedReportBranding,
    'audienceMode' | 'primaryLogoUrl' | 'secondaryLogoUrl' | 'primaryLogoAlt' | 'secondaryLogoAlt'
  >;
  /** testid suffix (typically the site id) so multiple cards stay individually targetable. */
  testIdSuffix?: string;
  className?: string;
}

/** Hide a logo <img> on load failure so cards never show broken images. */
function hideOnError(e: React.SyntheticEvent<HTMLImageElement>): void {
  e.currentTarget.style.display = 'none';
}

export const SiteCardLogo: React.FC<SiteCardLogoProps> = ({
  branding,
  testIdSuffix,
  className,
}) => {
  // Customer association = customer audience AND an actual customer mark.
  const isCustomer =
    branding.audienceMode === 'customer' && Boolean(branding.secondaryLogoUrl);

  // Customer sites surface the customer mark ONLY (no co-branding on the
  // compact list card). Everything else surfaces the company mark.
  const logoUrl = isCustomer ? branding.secondaryLogoUrl : branding.primaryLogoUrl;
  const logoAlt = isCustomer ? branding.secondaryLogoAlt : branding.primaryLogoAlt;
  const variant = isCustomer ? 'customer' : 'company';

  // No URL to show — collapse entirely (no empty box / broken image).
  if (!logoUrl) return null;

  const suffix = testIdSuffix ? `-${testIdSuffix}` : '';

  return (
    <LogoBackingChip
      className={`shrink-0 ${className ?? ''}`}
      whiteOnDark
      data-testid={`site-card-logo${suffix}`}
      data-logo-variant={variant}
      aria-hidden="true"
    >
      <img
        src={toRelativeLogoMediaUrl(logoUrl)}
        alt={logoAlt}
        className="h-4 max-w-[64px] object-contain"
        onError={hideOnError}
        data-testid={`site-card-logo-${variant}${suffix}`}
      />
    </LogoBackingChip>
  );
};
